import { useCallback, useState } from 'react';
import type {
  AddMasterSensorsRequest,
  AddMasterSensorsResponse,
  AddSensorsRequest,
  AddSensorsResponse,
  CreateCustomGroupRequest,
  CustomerSensor,
  CustomGroup,
  CustomGroupDetail,
  CustomGroupListItem,
  CustomGroupSensor,
  SuggestionGroup,
  UpdateCustomGroupRequest,
} from '@metron/shared';
import { getApiUrl } from '../config/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { toErrorMessage } from '@metron/shared';

interface ListCustomGroupsResponse {
  customGroups: CustomGroupListItem[];
  total: number;
}

export function useCustomGroups() {
  const { getAccessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error('No access token available');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessToken]);

  const listCustomGroups = useCallback(
    async (customerId: number): Promise<ListCustomGroupsResponse> => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = await authHeaders();
        const response = await fetch(getApiUrl(`custom-groups?customerId=${customerId}`), {
          headers,
        });
        if (!response.ok) throw new Error(`Failed to list custom groups: ${response.statusText}`);
        return await response.json();
      } catch (err) {
        const message = toErrorMessage(err, 'Failed to list custom groups');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [authHeaders],
  );

  const getCustomGroup = useCallback(async (id: number): Promise<CustomGroupDetail> => {
    const headers = await authHeaders();
    const response = await fetch(getApiUrl(`custom-groups/${id}`), { headers });
    if (!response.ok) throw new Error(`Failed to get custom group: ${response.statusText}`);
    return await response.json();
  }, [authHeaders]);

  const createCustomGroup = useCallback(
    async (data: CreateCustomGroupRequest): Promise<{ customGroup: CustomGroup }> => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = await authHeaders();
        const response = await fetch(getApiUrl('custom-groups'), {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `Failed to create custom group: ${response.statusText}`,
          );
        }
        return await response.json();
      } catch (err) {
        const message = toErrorMessage(err, 'Failed to create custom group');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [authHeaders],
  );

  const updateCustomGroup = useCallback(
    async (id: number, data: UpdateCustomGroupRequest): Promise<CustomGroupDetail> => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = await authHeaders();
        const response = await fetch(getApiUrl(`custom-groups/${id}`), {
          method: 'PUT',
          headers,
          body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error(`Failed to update custom group: ${response.statusText}`);
        return await response.json();
      } catch (err) {
        const message = toErrorMessage(err, 'Failed to update custom group');
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [authHeaders],
  );

  const deleteCustomGroup = useCallback(async (id: number): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const response = await fetch(getApiUrl(`custom-groups/${id}`), { method: 'DELETE', headers });
      if (!response.ok) throw new Error(`Failed to delete custom group: ${response.statusText}`);
    } catch (err) {
      const message = toErrorMessage(err, 'Failed to delete custom group');
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders]);

  const getSensors = useCallback(async (id: number): Promise<{ sensors: CustomGroupSensor[] }> => {
    const headers = await authHeaders();
    const response = await fetch(getApiUrl(`custom-groups/${id}/sensors`), { headers });
    if (!response.ok) throw new Error(`Failed to get sensors: ${response.statusText}`);
    return await response.json();
  }, [authHeaders]);

  const addSubmeters = useCallback(
    async (id: number, data: AddSensorsRequest): Promise<AddSensorsResponse> => {
      const headers = await authHeaders();
      const response = await fetch(getApiUrl(`custom-groups/${id}/sensors`), {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error(`Failed to add submeters: ${response.statusText}`);
      return await response.json();
    },
    [authHeaders],
  );

  const removeSubmeter = useCallback(async (id: number, sensorId: number): Promise<void> => {
    const headers = await authHeaders();
    const response = await fetch(getApiUrl(`custom-groups/${id}/sensors/${sensorId}`), {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) throw new Error(`Failed to remove submeter: ${response.statusText}`);
  }, [authHeaders]);

  const addMasterSensors = useCallback(
    async (id: number, data: AddMasterSensorsRequest): Promise<AddMasterSensorsResponse> => {
      const headers = await authHeaders();
      const response = await fetch(getApiUrl(`custom-groups/${id}/master-sensors`), {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error(`Failed to add master sensors: ${response.statusText}`);
      return await response.json();
    },
    [authHeaders],
  );

  const removeMasterSensor = useCallback(async (id: number, sensorId: number): Promise<void> => {
    const headers = await authHeaders();
    const response = await fetch(getApiUrl(`custom-groups/${id}/master-sensors/${sensorId}`), {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) throw new Error(`Failed to remove master sensor: ${response.statusText}`);
  }, [authHeaders]);

  const getCustomerSensors = useCallback(
    async (customerId: number): Promise<{ sensors: CustomerSensor[] }> => {
      const headers = await authHeaders();
      const response = await fetch(getApiUrl(`customers/${customerId}/sensors`), { headers });
      if (!response.ok) throw new Error(`Failed to get customer sensors: ${response.statusText}`);
      return await response.json();
    },
    [authHeaders],
  );

  const getSuggestions = useCallback(
    async (customerId: number): Promise<{ suggestions: SuggestionGroup[] }> => {
      const headers = await authHeaders();
      const response = await fetch(
        getApiUrl(`custom-groups/suggestions?customerId=${customerId}`),
        { headers },
      );
      if (!response.ok) throw new Error(`Failed to get suggestions: ${response.statusText}`);
      return await response.json();
    },
    [authHeaders],
  );

  const getSourceGroupSensors = useCallback(
    async (sensorGroupId: number): Promise<{ sensorIds: number[] }> => {
      const headers = await authHeaders();
      const response = await fetch(getApiUrl(`custom-groups/source-sensors/${sensorGroupId}`), {
        headers,
      });
      if (!response.ok) {
        throw new Error(`Failed to get source group sensors: ${response.statusText}`);
      }
      return await response.json();
    },
    [authHeaders],
  );

  return {
    isLoading,
    error,
    listCustomGroups,
    getCustomGroup,
    createCustomGroup,
    updateCustomGroup,
    deleteCustomGroup,
    getSensors,
    addSubmeters,
    removeSubmeter,
    addMasterSensors,
    removeMasterSensor,
    getCustomerSensors,
    getSuggestions,
    getSourceGroupSensors,
  };
}
